import React, { useState, useEffect } from "react";
import { Grid } from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { Button, ScrollToTop } from "components";
import { BackIcon } from "icons";
import { localStorageHelper, handleConvertDateYear } from "utils";

import { Details } from "models/stressCheck/stressCheckDetails";
import { StressCheckDetailsActionCreators } from "redux/rootActions";
import { stressCheckDetailsDataResponse } from "redux/stressCheck/stressCheckDetails/stressCheckDetailsStates";

import {
  ItemsContainer,
  LeftItem,
  TextTitle,
  TitleContainer,
  TitleWrapper,
  Dot,
  StressStatus,
  DetailsTitleWrapper,
  Tooltip4AlertMobile,
} from "./Stress.style";
import { StressCheckEdit } from "./StressCheckEdit";

const { useBreakpoint } = Grid;

interface INewStressCheck {
  id: any;
  handleCancel: () => void;
}

interface IStressStyles {
  alert: any;
  background: string;
  color: string;
  borderColor: string;
  status: string;
}

const StressDetails: React.FC<INewStressCheck> = ({ handleCancel, id }) => {
  const currentLanguage = localStorageHelper.getItem("i18nextLng");
  const { t }: any = useTranslation("translation", {
    keyPrefix: "stress-check",
  });
  const { t: t1 }: any = useTranslation("translation", {
    keyPrefix: "mental-health",
  });

  const dispatch = useDispatch();
  const stressCheckDetailsData = useSelector(stressCheckDetailsDataResponse);

  const [editing, setEditing] = useState<boolean>(false);
  const [startDate, setStartDate] = useState(
    moment().subtract(1, "M").toDate()
  );
  const [isVisibleTooltip, setIsVisibleTooltip] = useState<boolean>(false);
  const [detailsData, setDetailsData] = useState<Details>({
    color_code_status: "",
    stress_check_type_id: 1,
    name: "",
    status: 0,
    examination_period: "",
    implement_status: "",
    id: id,
  });
  const [stressStyles, setStressStyles] = useState<IStressStyles>({
    alert: "",
    background: "",
    color: "",
    borderColor: "",
    status: "",
  });
  const displayCondition = detailsData.implement_status === "実施待ち";

  const handleToggleTooltip = () => {
    setIsVisibleTooltip(!isVisibleTooltip);
  };
  const screens = useBreakpoint();

  const returnAlertStyles = (status: string) => {
    switch (status) {
      case "実施完了":
        setStressStyles({
          alert: t("stress-completed"),
          background: "#F2F2F2",
          color: "#525252",
          borderColor: "#666666",
          status: t1("completed"),
        });
        break;
      case "実施待ち":
        setStressStyles({
          alert:
            handleConvertDateYear(
              moment(startDate).format("YYYY-MM-DD"),
              currentLanguage
            ) + t("stress-check-will-be-implemented"),
          background: "rgba(235, 195, 0, 0.15)",
          color: "#997F00",
          borderColor: "#EBC300",
          status: t1("waiting-implementation"),
        });
        break;
      case "実施中":
        setStressStyles({
          alert: t("stress-in-progress"),
          background: "rgba(42, 199, 105, 0.15)",
          color: "#1C8345",
          borderColor: "#2AC769",
          status: t1("in-progress"),
        });
        break;
    }
  };

  useEffect(() => {
    if (stressCheckDetailsData && stressCheckDetailsData?.result) {
      const {
        name,
        stress_check_type_id,
        examination_period,
        status,
        color_code_status,
        implement_status,
        id,
      } = stressCheckDetailsData?.result;
      setDetailsData({
        name,
        stress_check_type_id,
        color_code_status,
        status,
        examination_period,
        implement_status,
        id,
      });
      const examinationPeriod: string[] = examination_period.split("〜");
      if (examinationPeriod.length) {
        setStartDate(moment(examinationPeriod[0]).toDate());
      }
    }
  }, [stressCheckDetailsData]);

  useEffect(() => {
    if (id) {
      dispatch(
        StressCheckDetailsActionCreators.stressCheckDetailsAction({
          id: id,
          language_code: currentLanguage,
        })
      );
    }
  }, [id]);

  useEffect(() => {
    if (detailsData) {
      returnAlertStyles(detailsData.implement_status);
    }
  }, [detailsData]);

  useEffect(() => {
    setEditing(false);
    dispatch(StressCheckDetailsActionCreators.handleResetDataAction());
  }, []);

  return (
    <>
      <ScrollToTop />
      <DetailsTitleWrapper
        style={{
          marginBottom: editing ? 24 : 16,
        }}
      >
        <div
          style={{
            fontSize: 16,
            lineHeight: "23px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {!editing && (
            <BackIcon
              style={{
                cursor: "pointer",
              }}
              onClick={handleCancel}
            />
          )}
          {t1("implementation-details")}
        </div>
        {!editing &&
          (screens.md ? (
            <div
              className="alert"
              style={{
                border: `2px solid ${stressStyles.borderColor}`,
                background: stressStyles.background,
                color: stressStyles.color,
              }}
            >
              <div>{stressStyles.alert}</div>
            </div>
          ) : (
            <div style={{ position: "relative" }}>
              {isVisibleTooltip && (
                <Tooltip4AlertMobile>
                  <div>{stressStyles.alert}</div>
                </Tooltip4AlertMobile>
              )}
              <StressStatus
                style={{
                  background: "#FFFFFF",
                }}
                onClick={handleToggleTooltip}
              >
                <Dot
                  style={{
                    background: stressStyles.borderColor,
                    width: "14px",
                    height: "14px",
                  }}
                />
                <div>{stressStyles.status}</div>
              </StressStatus>
            </div>
          ))}
      </DetailsTitleWrapper>
      <div
        style={{
          padding: "16px 16px 32px",
          background: "#FFFFFF",
          borderRadius: 8,
        }}
      >
        {editing ? (
          <StressCheckEdit
            detailsData={detailsData}
            handleCancel={handleCancel}
            setEdit={setEditing}
          />
        ) : (
          <>
            <TitleContainer
              style={{
                paddingBottom: 16,
                marginBottom: 0,
                borderBottom: "1px solid #F2F2F2",
              }}
            >
              <TitleWrapper>
                <TextTitle>{detailsData.name}</TextTitle>
                {screens.md && (
                  <StressStatus>
                    <Dot
                      style={{
                        background: stressStyles.borderColor,
                        width: "14px",
                        height: "14px",
                      }}
                    />
                    <div>{stressStyles.status}</div>
                  </StressStatus>
                )}
              </TitleWrapper>
              {screens.md && displayCondition && (
                <Button
                  color="#FFFFFF"
                  fontSize={16}
                  fontWeight={700}
                  lineHeight="16px"
                  background={"#2F8CAE"}
                  padding="11px 48px 13px"
                  name={t1("edit")}
                  bdr="8px"
                  border="none"
                  onClick={() => setEditing(true)}
                />
              )}
            </TitleContainer>
            <ItemsContainer
              style={{
                borderBottom: "none",
                paddingBottom: 0,
              }}
            >
              <LeftItem>{t1("examination-period")}</LeftItem>
              <div
                style={{
                  flex: 1,
                  background: "#F2F2F2",
                  borderRadius: "6px",
                  fontWeight: 500,
                  fontSize: 16,
                  lineHeight: "16px",
                  padding: 12,
                  width: "100%",
                }}
              >
                {detailsData.examination_period}
              </div>
            </ItemsContainer>

            {!screens.md && displayCondition && (
              <Button
                color="#FFFFFF"
                fontSize={16}
                fontWeight={700}
                lineHeight="16px"
                background={"#2F8CAE"}
                padding="11px 48px 13px"
                name={t1("edit")}
                bdr="8px"
                border="none"
                onClick={() => setEditing(true)}
                margin="48px auto 0px"
                width="100%"
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default StressDetails;
